import { useState } from 'react';
import { Button, Checkbox, EntityChip, LiveStepList, type LiveOperationStep } from '@/components/ui';
import { useExpansionMemory } from '@/components/layout/workspace/useExpansionMemory';

const TITLES = [
  'Получить записи',
  'Прочитать связи',
  'Проверить идентификаторы',
  'Сверить состояния источников',
  'Найти пропущенные связи',
  'Проверить временные интервалы',
  'Сопоставить результаты',
  'Составить итог',
];
export function CompactStepsDemo({
  scope,
  onEntityOpen,
}: {
  scope: string;
  onEntityOpen: (id: string) => void;
}) {
  const [current, setCurrent] = useState(3),
    [failed, setFailed] = useState(false),
    [failNext, setFailNext] = useState(false),
    [ai, setAi] = useState(false);
  const [expanded, setExpanded] = useExpansionMemory(scope + '.compact', ['compact-3']);
  const complete = current === TITLES.length;
  const steps: LiveOperationStep[] = TITLES.map((label, index) => ({
    id: 'compact-' + index,
    label,
    status: index < current ? 'done' : index > current ? 'pending' : failed ? 'failed' : 'active',
    progress: 57,
    note: index === current ? (failed ? 'Нет ответа' : '57 %') : undefined,
    details: (
      <div className="flex flex-col gap-3">
        <p>
          {index < current
            ? 'Шаг завершён на вымышленных данных этого запуска.'
            : index === current
              ? failed
                ? 'Источник Б не ответил. Полученные результаты сохранены; повтори текущий шаг.'
                : 'Проверены 4 из 7 записей. Доля относится к текущему шагу.'
              : 'Подробности появятся, когда проверка дойдёт до этого шага.'}
        </p>
        <div>
          <EntityChip kind="task" name="Запись А-12" onClick={() => onEntityOpen('record-a12')} />
        </div>
      </div>
    ),
  }));
  return (
    <div data-slot="compact-steps-demo" className="flex min-w-0 flex-col gap-4">
      <p className="text-sm text-fg-muted">
        Пробная длинная проверка. Шаги можно пройти вручную; подробности раскрываются на месте.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={complete}
          onClick={() => {
            if (failed) {
              setFailed(false);
              setFailNext(false);
            } else if (failNext) setFailed(true);
            else setCurrent((value) => Math.min(TITLES.length, value + 1));
          }}
        >
          {failed ? 'Повторить шаг' : 'Следующий шаг'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setCurrent(3);
            setFailed(false);
            setFailNext(false);
          }}
        >
          Сначала
        </Button>
      </div>
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        <Checkbox
          label="Проверку запустил ИИ"
          checked={ai}
          onCheckedChange={(value) => setAi(value === true)}
        />
        <Checkbox
          label="Сбой текущего шага"
          checked={failNext}
          disabled={failed || complete}
          onCheckedChange={(value) => setFailNext(value === true)}
        />
      </div>
      <LiveStepList
        variant="compact"
        steps={steps}
        initiator={ai ? 'ai' : 'system'}
        expanded={expanded}
        onExpandedChange={setExpanded}
        headingLevel={4}
        summary={
          complete ? (
            <p>
              <strong>Проверка завершена.</strong> Все восемь шагов пройдены, ошибок не найдено.
            </p>
          ) : failed ? (
            <p>
              <strong>Проверка остановлена.</strong> Повтори текущий шаг; прежние результаты сохранены.
            </p>
          ) : undefined
        }
      />
    </div>
  );
}
